import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useRef, useEffect, useState } from 'react';
import { refreshSupplierProfile, fetchLetterhead } from '../slices/authSlice';

/**
 * Required fields for a complete supplier profile
 * These match the validations in Settings.jsx
 */
export const REQUIRED_PROFILE_FIELDS = [
  'name',                            // Company Name
  'tin',                             // TIN Number
  'idType',                          // Registration ID Type
  'idValue',                         // Registration ID Value
  'contactNumber',                   // Contact Number
  'address.addressLine0',            // Address Line 1
  'address.cityName',                // City
  'address.postalZone',              // Postal Code
  'address.state',                   // State
  'address.country',                 // Country
  'msicCode',                        // MSIC Code
  'businessActivityDescription'      // Business Activity Description
];

/**
 * Check if a specific field in the supplier profile has a value
 * @param {Object} profile - The supplier profile object
 * @param {string} fieldPath - Dot-notation path to the field (e.g., 'address.cityName')
 * @returns {boolean} - True if field has a non-empty value
 */
export const hasFieldValue = (profile, fieldPath) => {
  if (!profile) return false;
  
  const keys = fieldPath.split('.');
  let value = profile;
  
  for (const key of keys) {
    if (value === null || value === undefined) return false;
    value = value[key];
  }
  
  // Check if value is non-empty string or valid value
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  
  return value !== null && value !== undefined;
};

/**
 * Calculate profile completeness percentage
 * @param {Object} profile - The supplier profile object
 * @returns {Object} - { percentage, completed, total, missingFields }
 */
export const calculateProfileCompleteness = (profile) => {
  if (!profile) {
    return { percentage: 0, completed: 0, total: REQUIRED_PROFILE_FIELDS.length, missingFields: REQUIRED_PROFILE_FIELDS };
  }
  
  let completed = 0;
  const missingFields = [];
  
  REQUIRED_PROFILE_FIELDS.forEach(field => {
    if (hasFieldValue(profile, field)) {
      completed++;
    } else {
      missingFields.push(field);
    }
  });
  
  const percentage = Math.round((completed / REQUIRED_PROFILE_FIELDS.length) * 100);
  
  return {
    percentage,
    completed,
    total: REQUIRED_PROFILE_FIELDS.length,
    missingFields
  };
};

/**
 * Check if supplier profile is complete (all required fields filled)
 * @param {Object} profile - The supplier profile object
 * @returns {boolean} - True if profile is complete
 */
export const isProfileComplete = (profile) => {
  const { percentage } = calculateProfileCompleteness(profile);
  return percentage === 100;
};

/**
 * Get human-readable field name
 * @param {string} fieldPath - Dot-notation field path
 * @returns {string} - Human-readable field name
 */
export const getFieldDisplayName = (fieldPath) => {
  const fieldNames = {
    'name': 'Company Name',
    'tin': 'TIN Number',
    'idType': 'Registration ID Type',
    'idValue': 'Registration ID Value',
    'contactNumber': 'Contact Number',
    'address.addressLine0': 'Address Line 1',
    'address.cityName': 'City',
    'address.postalZone': 'Postal Code',
    'address.state': 'State',
    'address.country': 'Country',
    'msicCode': 'MSIC Code',
    'businessActivityDescription': 'Business Activity Description'
  };
  
  return fieldNames[fieldPath] || fieldPath;
};

/**
 * Helper function to transform supplier profile to invoice supplier format
 * @param {Object} profile - The supplier profile from Redux state
 * @returns {Object} - Formatted supplier data for invoices
 */
export const getSupplierDataForInvoice = (profile) => {
  if (!profile) return null;
  
  return {
    name: profile.name || '',
    tin: profile.tin || '',
    registrationType: profile.idType || '',
    registrationNumber: profile.idValue || '',
    contactNumber: profile.contactNumber || '',
    email: profile.email || '',
    address: {
      addressLine0: profile.address?.addressLine0 || '',
      addressLine1: profile.address?.addressLine1 || '',
      addressLine2: profile.address?.addressLine2 || '',
      cityName: profile.address?.cityName || '',
      postalZone: profile.address?.postalZone || '',
      state: profile.address?.state || '',
      country: profile.address?.country || 'MYS'
    },
    msicCode: profile.msicCode || '',
    businessActivityDescription: profile.businessActivityDescription || '',
    sstRegistrationNumber: profile.sstRegistrationNumber || '',
    tourismTaxRegistrationNumber: profile.tourismTaxRegistrationNumber || '',
    certifiedExporterAuthNumber: profile.certifiedExporterAuthNumber || '',
    letterhead: profile.letterhead || '',
    bankName: profile.bankName || '',
    bankAccountNumber: profile.bankAccountNumber || ''
  };
};

/**
 * Compare draft supplier data with current profile to detect differences
 * @param {Object} draftSupplierData - Supplier data from draft invoice
 * @param {Object} profile - Current supplier profile
 * @returns {Object} - Comparison result with differences and hasChanges flag
 */
export const compareDraftWithProfile = (draftSupplierData, profile) => {
  if (!profile || !draftSupplierData) {
    return { hasChanges: false, differences: [] };
  }
  
  const differences = [];
  
  // Compare key fields
  const fieldsToCompare = [
    { key: 'name', label: 'Company Name', draft: draftSupplierData.name, profile: profile.name },
    { key: 'tin', label: 'TIN', draft: draftSupplierData.tin, profile: profile.tin },
    { key: 'registrationType', label: 'ID Type', draft: draftSupplierData.registrationType, profile: profile.idType },
    { key: 'registrationNumber', label: 'ID Value', draft: draftSupplierData.registrationNumber, profile: profile.idValue },
    { key: 'contactNumber', label: 'Contact Number', draft: draftSupplierData.contactNumber, profile: profile.contactNumber },
    { key: 'email', label: 'Email', draft: draftSupplierData.email, profile: profile.email },
    { key: 'msicCode', label: 'MSIC Code', draft: draftSupplierData.msicCode, profile: profile.msicCode },
    { key: 'businessActivityDescription', label: 'Business Activity', draft: draftSupplierData.businessActivityDescription, profile: profile.businessActivityDescription },
    { key: 'sstRegistrationNumber', label: 'SST Number', draft: draftSupplierData.sstRegistrationNumber, profile: profile.sstRegistrationNumber },
    { key: 'tourismTaxRegistrationNumber', label: 'TTX Number', draft: draftSupplierData.tourismTaxRegistrationNumber, profile: profile.tourismTaxRegistrationNumber },
  ];
  
  fieldsToCompare.forEach(field => {
    const draftValue = (field.draft || '').toString().trim();
    const profileValue = (field.profile || '').toString().trim();
    
    if (draftValue !== profileValue) {
      differences.push({
        field: field.key,
        label: field.label,
        draftValue: field.draft || '',
        profileValue: field.profile || ''
      });
    }
  });
  
  // Compare address fields
  const addressFields = [
    { key: 'addressLine0', label: 'Address Line 1' },
    { key: 'addressLine1', label: 'Address Line 2' },
    { key: 'addressLine2', label: 'Address Line 3' },
    { key: 'cityName', label: 'City' },
    { key: 'postalZone', label: 'Postal Code' },
    { key: 'state', label: 'State' },
    { key: 'country', label: 'Country' }
  ];
  
  addressFields.forEach(addrField => {
    const draftValue = (draftSupplierData.address?.[addrField.key] || '').toString().trim();
    const profileValue = (profile.address?.[addrField.key] || '').toString().trim();
    
    if (draftValue !== profileValue) {
      differences.push({
        field: `address.${addrField.key}`,
        label: addrField.label,
        draftValue: draftSupplierData.address?.[addrField.key] || '',
        profileValue: profile.address?.[addrField.key] || ''
      });
    }
  });
  
  return {
    hasChanges: differences.length > 0,
    differences,
    differenceCount: differences.length
  };
};

/**
 * Extract supplier data from UBL invoice document
 * @param {Object} ublDocument - The UBL document from draft invoice
 * @returns {Object} - Formatted supplier data
 */
export const extractSupplierFromUbl = (ublDocument) => {
  if (!ublDocument) return null;
  
  const invoice = ublDocument.Invoice?.[0];
  if (!invoice) return null;
  
  const supplierParty = invoice.AccountingSupplierParty?.[0]?.Party?.[0];
  if (!supplierParty) return null;
  
  const partyIdentifications = supplierParty.PartyIdentification || [];
  const postalAddress = supplierParty.PostalAddress?.[0];
  const contact = supplierParty.Contact?.[0];
  const legalEntity = supplierParty.PartyLegalEntity?.[0];
  const industryCode = supplierParty.IndustryClassificationCode?.[0];
  
  // Find ID type and value
  const tinEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'TIN');
  const brnEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'BRN');
  const nricEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'NRIC');
  const passportEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'PASSPORT');
  const armyEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'ARMY');
  const sstEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'SST');
  const ttxEntry = partyIdentifications.find(pid => pid.ID?.[0]?.schemeID === 'TTX');
  
  let idType = '';
  let idValue = '';
  
  if (brnEntry) {
    idType = 'BRN';
    idValue = brnEntry.ID?.[0]?._ || '';
  } else if (nricEntry) {
    idType = 'NRIC';
    idValue = nricEntry.ID?.[0]?._ || '';
  } else if (passportEntry) {
    idType = 'PASSPORT';
    idValue = passportEntry.ID?.[0]?._ || '';
  } else if (armyEntry) {
    idType = 'ARMY';
    idValue = armyEntry.ID?.[0]?._ || '';
  }
  
  return {
    name: legalEntity?.RegistrationName?.[0]?._ || '',
    tin: tinEntry?.ID?.[0]?._ || '',
    registrationType: idType,
    registrationNumber: idValue,
    contactNumber: contact?.Telephone?.[0]?._ || '',
    email: contact?.ElectronicMail?.[0]?._ || '',
    address: {
      addressLine0: postalAddress?.AddressLine?.[0]?.Line?.[0]?._ || '',
      addressLine1: postalAddress?.AddressLine?.[1]?.Line?.[0]?._ || '',
      addressLine2: postalAddress?.AddressLine?.[2]?.Line?.[0]?._ || '',
      cityName: postalAddress?.CityName?.[0]?._ || '',
      postalZone: postalAddress?.PostalZone?.[0]?._ || '',
      state: postalAddress?.CountrySubentityCode?.[0]?._ || '',
      country: postalAddress?.Country?.[0]?.IdentificationCode?.[0]?._ || 'MYS'
    },
    msicCode: industryCode?._ || '',
    businessActivityDescription: industryCode?.name || '',
    sstRegistrationNumber: sstEntry?.ID?.[0]?._ || '',
    tourismTaxRegistrationNumber: ttxEntry?.ID?.[0]?._ || ''
  };
};

/**
 * Custom hook to check supplier profile status with real-time synchronization
 * @returns {Object} - Profile status, helper functions, and refresh capability
 */
const useSupplierProfile = () => {
  const dispatch = useDispatch();
  const { supplierProfile, user, letterhead, isLoading: authLoading } = useSelector((state) => state.auth);
  const previousProfileRef = useRef(null);
  const letterheadRequestedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(authLoading || false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Error tracking to prevent duplicate error messages
  const errorTrackingRef = useRef({
    lastErrorMessage: null,
    lastErrorTime: 0,
    errorCount: 0,
    retryAttempt: 0,
    maxRetries: 3,
    errorSuppressionWindow: 60000, // 60 seconds
    isSuppressed: false
  });
  
  // Track profile loading state
  useEffect(() => {
    setIsLoading(authLoading);
  }, [authLoading]);
  
  // Track when profile is initialized (loaded at least once)
  useEffect(() => {
    if (supplierProfile && !isInitialized) {
      setIsInitialized(true);
    }
  }, [supplierProfile, isInitialized]);

  // Load the letterhead once per session. It no longer arrives with the profile (too large), so it is
  // fetched lazily the first time anything needs a profile. The ref guard stops every consumer of this
  // hook firing its own request.
  useEffect(() => {
    if (!user || letterhead || letterheadRequestedRef.current) return;
    letterheadRequestedRef.current = true;
    dispatch(fetchLetterhead());
  }, [user, letterhead, dispatch]);
  
  // Track profile changes for real-time synchronization
  useEffect(() => {
    if (supplierProfile && previousProfileRef.current) {
      // Check if profile has changed
      const hasChanged = JSON.stringify(previousProfileRef.current) !== JSON.stringify(supplierProfile);
      if (hasChanged) {
        // Dispatch a custom event to notify components of profile update
        window.dispatchEvent(new CustomEvent('supplierProfileUpdated', {
          detail: { 
            profile: supplierProfile, 
            previousProfile: previousProfileRef.current,
            timestamp: Date.now()
          }
        }));
      }
    }
    previousProfileRef.current = supplierProfile;
  }, [supplierProfile]);
  
  // Function to refresh supplier profile from server
  const refreshProfile = useCallback(async (options = {}) => {
    const { 
      silent = false,           // Don't log errors if true
      suppressDuplicates = true, // Suppress duplicate error messages
      throwOnError = true       // Whether to throw error after logging (default true for backward compatibility)
    } = options;
    
    try {
      const result = await dispatch(refreshSupplierProfile()).unwrap();
      
      // Reset error tracking on success
      errorTrackingRef.current.errorCount = 0;
      errorTrackingRef.current.retryAttempt = 0;
      errorTrackingRef.current.isSuppressed = false;
      
      return result;
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      const now = Date.now();
      const tracking = errorTrackingRef.current;
      
      // Check if this is a duplicate error (same message within suppression window)
      const isDuplicate = suppressDuplicates && 
        tracking.lastErrorMessage === errorMessage &&
        (now - tracking.lastErrorTime) < tracking.errorSuppressionWindow;
      
      if (!isDuplicate) {
        // Not a duplicate, log the error
        if (!silent) {
          console.error('Failed to refresh supplier profile:', errorMessage);
        }
        
        // Update tracking
        tracking.lastErrorMessage = errorMessage;
        tracking.lastErrorTime = now;
        tracking.errorCount++;
      } else {
        // Duplicate error, increment count but don't log
        tracking.errorCount++;
      }
      
      // Check if we should suppress further errors (too many retries)
      if (tracking.errorCount >= tracking.maxRetries) {
        tracking.isSuppressed = true;
        if (!silent && !isDuplicate) {
          console.warn('Supplier profile refresh error suppression activated after', tracking.maxRetries, 'failures');
        }
      }
      
      // Only throw error if throwOnError is true
      if (throwOnError) {
        throw error;
      }
      
      // Return null to indicate failure without throwing
      return null;
    }
  }, [dispatch]);
  
  // Get the last updated timestamp from profile or return current time if not available
  const getProfileLastUpdated = useCallback(() => {
    // Check if profile has updatedAt field from server
    if (supplierProfile?.updatedAt) {
      return new Date(supplierProfile.updatedAt).getTime();
    }
    // Fallback to local timestamp if available
    if (supplierProfile?._localTimestamp) {
      return supplierProfile._localTimestamp;
    }
    return null;
  }, [supplierProfile]);
  
  // Check if profile is newer than a given timestamp (e.g., draft creation time)
  // CRITICAL FIX: Also check alwaysUseProfileAsSupplier preference
  const shouldSyncWithProfile = useCallback((draftTimestamp, formValues = {}, fieldPrefix = 'supplier') => {
    // Check user preference first - if alwaysUseProfileAsSupplier is true, always sync
    const userPrefs = user?.userPreferences || {};
    const alwaysUseProfile = userPrefs.alwaysUseProfileAsSupplier === true;
    
    if (alwaysUseProfile) {
      // Only sync if profile has at least some data (not empty)
      const hasProfileData = supplierProfile?.name || supplierProfile?.tin;
      return hasProfileData;
    }
    
    // Otherwise, check if supplier fields in form are empty
    const supplierFields = [
      `${fieldPrefix}Name`,
      `${fieldPrefix}TIN`,
      `${fieldPrefix}RegistrationIdType`,
      `${fieldPrefix}RegistrationIdValue`,
      `${fieldPrefix}ContactNumber`,
      `${fieldPrefix}MSICCode`,
      `${fieldPrefix}BusinessActivityDescription`
    ];
    
    // Check if any supplier field has a value
    const hasAnySupplierValue = supplierFields.some(field => {
      const value = formValues[field];
      return value !== undefined && value !== null && value !== '';
    });
    
    // Also check address fields
    const addressFields = [
      `${fieldPrefix}Address.addressLine0`,
      `${fieldPrefix}Address.cityName`,
      `${fieldPrefix}Address.postalZone`,
      `${fieldPrefix}Address.country`,
      `${fieldPrefix}Address.state`
    ];
    
    const hasAnyAddressValue = addressFields.some(fieldPath => {
      const keys = fieldPath.split('.');
      let value = formValues;
      for (const key of keys) {
        if (value === undefined || value === null) return false;
        value = value[key];
      }
      return value !== undefined && value !== null && value !== '';
    });
    
    // If form already has supplier data, don't auto-sync
    if (hasAnySupplierValue || hasAnyAddressValue) {
      return false;
    }
    
    // Otherwise, check timestamp if provided
    const profileTimestamp = getProfileLastUpdated();
    if (!profileTimestamp || !draftTimestamp) {
      // If we can't determine timestamps, default to checking if profile is complete
      return isProfileComplete(supplierProfile);
    }
    // Profile is newer than draft if updated after draft was created
    return profileTimestamp > draftTimestamp;
  }, [getProfileLastUpdated, supplierProfile, user]);
  
  // Force sync supplier data into a form instance
  const syncSupplierToForm = useCallback((formInstance, options = {}) => {
    if (!supplierProfile || !formInstance) return false;
    
    const { 
      overwriteExisting = false,
      fieldPrefix = 'supplier',
      includeFields = null, // null means all, or array of specific fields
      excludeFields = [] 
    } = options;
    
    const supplierData = getSupplierDataForInvoice(supplierProfile);
    if (!supplierData) return false;
    
    const formValues = {};
    
    // Map supplier data to form fields
    const fieldMappings = {
      [`${fieldPrefix}Name`]: supplierData.name,
      [`${fieldPrefix}TIN`]: supplierData.tin,
      [`${fieldPrefix}RegistrationIdType`]: supplierData.registrationType,
      [`${fieldPrefix}RegistrationIdValue`]: supplierData.registrationNumber,
      [`${fieldPrefix}SSTRegistrationNumber`]: supplierData.sstRegistrationNumber,
      [`${fieldPrefix}TourismTaxRegistrationNumber`]: supplierData.tourismTaxRegistrationNumber,
      [`${fieldPrefix}ContactNumber`]: supplierData.contactNumber,
      [`${fieldPrefix}Email`]: supplierData.email,
      [`${fieldPrefix}MSICCode`]: supplierData.msicCode,
      [`${fieldPrefix}BusinessActivityDescription`]: supplierData.businessActivityDescription,
      [`${fieldPrefix}CertifiedExporterAuthNumber`]: supplierProfile?.certifiedExporterAuthNumber,
      [`${fieldPrefix}BankName`]: supplierData.bankName,
      [`${fieldPrefix}BankAccountNumber`]: supplierData.bankAccountNumber,
    };
    
    // Add address fields
    if (supplierData.address) {
      fieldMappings[`${fieldPrefix}Address`] = {
        addressLine0: supplierData.address.addressLine0,
        addressLine1: supplierData.address.addressLine1,
        addressLine2: supplierData.address.addressLine2,
        postalZone: supplierData.address.postalZone,
        cityName: supplierData.address.cityName,
        state: supplierData.address.state,
        country: supplierData.address.country || 'MYS',
      };
    }
    
    // Apply include/exclude filters
    Object.keys(fieldMappings).forEach(fieldName => {
      // Skip if in exclude list
      if (excludeFields.includes(fieldName)) return;
      
      // Skip if include list specified and field not in it
      if (includeFields && !includeFields.includes(fieldName)) return;
      
      // Check if field should be overwritten
      if (!overwriteExisting) {
        const currentValue = formInstance.getFieldValue(fieldName);
        // Skip if field already has a value
        if (currentValue !== undefined && currentValue !== null && currentValue !== '') return;
      }
      
      formValues[fieldName] = fieldMappings[fieldName];
    });
    
    // Set form values
    formInstance.setFieldsValue(formValues);
    
    return true;
  }, [supplierProfile]);
  
  const completeness = calculateProfileCompleteness(supplierProfile);
  const isComplete = completeness.percentage === 100;
  
  // Get supplier data formatted for invoice initialization
  const getInvoiceSupplierData = useCallback(() => {
    return getSupplierDataForInvoice(supplierProfile);
  }, [supplierProfile]);
  
  return {
    // The letterhead is stored separately (state.auth.letterhead) because profile responses no longer
    // carry the image. Merged back in here so consumers reading profile.letterhead keep working
    // unchanged. supplierProfile.letterhead is the pre-migration fallback.
    profile: { ...supplierProfile, letterhead: supplierProfile?.letterhead || letterhead || null },
    isComplete,
    completeness,
    hasFieldValue: (fieldPath) => hasFieldValue(supplierProfile, fieldPath),
    getMissingFields: () => completeness.missingFields.map(getFieldDisplayName),
    percentage: completeness.percentage,
    refreshProfile,
    getInvoiceSupplierData,
    getProfileLastUpdated,
    shouldSyncWithProfile,
    syncSupplierToForm,
    userPreferences: user?.userPreferences || {},
    isLoading,
    isInitialized
  };
};

export default useSupplierProfile;
